/**
 * Budget Progress Component
 *
 * Displays a visual progress bar showing budget utilization.
 * Shows approved amount, current spent, remaining, and percentage.
 * Color-coded: green < 80%, amber 80-100%, red > 100%
 */

import { renderIcon } from '../utils/icons';
import { formatCurrency } from '../types/expense-tracker';

/**
 * Budget progress options
 */
export interface BudgetProgressOptions {
  /** Approved budget amount */
  approvedAmount: number;
  /** Current spent amount (in CHF) */
  spentAmount: number;
  /** Currency for approved amount display */
  currency?: string;
  /** Show compact version (no labels) */
  compact?: boolean;
}

/**
 * Create a budget progress component
 */
export function createBudgetProgress(options: BudgetProgressOptions): HTMLElement {
  const { approvedAmount, spentAmount, currency = 'CHF', compact = false } = options;

  // Calculate percentage and remaining
  const percentage = approvedAmount > 0 ? (spentAmount / approvedAmount) * 100 : 0;
  const remaining = approvedAmount - spentAmount;
  const isOverBudget = remaining < 0;

  // Determine progress bar color
  let barColor = 'bg-green-500';
  let textColor = 'text-green-500';
  if (percentage >= 100) {
    barColor = 'bg-destructive';
    textColor = 'text-destructive';
  } else if (percentage >= 80) {
    barColor = 'bg-amber-500';
    textColor = 'text-amber-500';
  }

  const container = document.createElement('div');
  container.className = compact ? 'space-y-1' : 'space-y-2';

  if (!compact) {
    // Header row with labels
    const header = document.createElement('div');
    header.className = 'flex justify-between text-sm';
    header.innerHTML = `
      <span class="text-muted-foreground">Budget Used</span>
      <span class="font-medium">${formatCurrency(approvedAmount, currency)} / ${formatCurrency(spentAmount, 'CHF')}</span>
    `;
    container.appendChild(header);
  }

  // Progress bar
  const progressWrapper = document.createElement('div');
  progressWrapper.className = 'h-2 bg-muted rounded-full overflow-hidden';

  const progressBar = document.createElement('div');
  progressBar.className = `${barColor} h-full transition-all duration-300`;
  progressBar.style.width = `${Math.min(percentage, 100)}%`;

  progressWrapper.appendChild(progressBar);
  container.appendChild(progressWrapper);

  // Footer row with percentage and remaining
  const footer = document.createElement('div');
  footer.className = 'flex justify-between text-xs text-muted-foreground';

  const percentageSpan = document.createElement('span');
  percentageSpan.className = percentage >= 80 ? textColor : '';
  percentageSpan.textContent = `${percentage.toFixed(1)}% used`;

  const remainingSpan = document.createElement('span');
  if (isOverBudget) {
    remainingSpan.className = 'text-destructive flex items-center gap-1';
    remainingSpan.innerHTML = `
      ${renderIcon('alertTriangle', { size: 'xs' }).outerHTML}
      <span>${formatCurrency(Math.abs(remaining), 'CHF')} over budget</span>
    `;
  } else {
    remainingSpan.textContent = `${formatCurrency(remaining, 'CHF')} remaining`;
  }

  footer.appendChild(percentageSpan);
  footer.appendChild(remainingSpan);
  container.appendChild(footer);

  return container;
}

export default createBudgetProgress;
